'use client';

import clsx from 'clsx';
import { forwardRef, useId, type TextareaHTMLAttributes, type ReactNode } from 'react';
import styles from './Textarea.module.scss';

export interface TextareaProps extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'size'> {
  /** Label text for the textarea */
  label?: ReactNode;
  /** Helper text displayed below the textarea */
  helperText?: string;
  /** Error message to display */
  error?: string;
  /** Whether the textarea has an error */
  hasError?: boolean;
  /** Color variant: primary (green) for light backgrounds, secondary (white) for dark backgrounds */
  variant?: 'primary' | 'secondary';
  /** Whether the textarea should take full width */
  fullWidth?: boolean;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      label,
      helperText,
      error,
      hasError,
      variant = 'primary',
      fullWidth = false,
      className,
      id: providedId,
      disabled,
      required,
      placeholder,
      ...props
    },
    ref
  ) => {
    const resolvedPlaceholder = placeholder && required ? `${placeholder}*` : placeholder;
    const generatedId = useId();
    const id = providedId || generatedId;
    const errorId = `${id}-error`;
    const helperId = `${id}-helper`;

    const showError = hasError || !!error;
    const describedBy =
      [showError ? errorId : null, helperText ? helperId : null].filter(Boolean).join(' ') ||
      undefined;

    return (
      <div
        className={clsx(
          styles.wrapper,
          {
            [styles.fullWidth]: fullWidth,
            [styles.hasError]: showError,
            [styles.disabled]: disabled,
            [styles.secondary]: variant === 'secondary',
          },
          className
        )}
      >
        {label && (
          <label htmlFor={id} className={styles.label}>
            {label}
            {required && (
              <span className={styles.required} aria-hidden="true">
                *
              </span>
            )}
          </label>
        )}
        <textarea
          ref={ref}
          id={id}
          disabled={disabled}
          required={required}
          aria-invalid={showError}
          aria-describedby={describedBy}
          placeholder={resolvedPlaceholder}
          className={styles.textarea}
          {...props}
        />
        {(error || helperText) && (
          <div className={styles.messages}>
            {error && (
              <span id={errorId} className={styles.error} role="alert">
                {error}
              </span>
            )}
            {helperText && !error && (
              <span id={helperId} className={styles.helper}>
                {helperText}
              </span>
            )}
          </div>
        )}
      </div>
    );
  }
);

Textarea.displayName = 'Textarea';
